import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { ApiError, auth as authApi } from "@/lib/api";
import { clearSession, loadSession, saveSession } from "@/lib/auth";
import type { Session } from "@/types";

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  signIn: (next: Session) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Bypass dev: cuando VITE_USE_MOCKS=true Y estamos en dev mode, auto-logueamos
// sin pasar por magic link (los emails reales apuntan a producción y rompen
// el testing local). El check de import.meta.env.DEV evita que un build de
// producción accidental con USE_MOCKS=true filtre el bypass a usuarios reales.
const DEV_AUTOLOGIN =
  import.meta.env.VITE_USE_MOCKS === "true" && import.meta.env.DEV;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (DEV_AUTOLOGIN) {
      // Sobreescribir siempre: si quedó una sesión real vieja en localStorage
      // (de un test previo del flow de magic link), la pisamos con la mock
      // para que el operador vea email/name consistentes.
      const mockSession: Session = {
        token: "mock_token",
        email: "dev@aliadigital.co",
        name: "Dev",
        tenantId: import.meta.env.VITE_TENANT_ID ?? "T001",
        // super_admin en dev para poder abrir cualquier vertical (/evergreen).
        rol: "super_admin",
      };
      saveSession(mockSession);
      setSession(mockSession);
      setLoading(false);
      return;
    }

    const stored = loadSession();
    if (!stored) {
      setLoading(false);
      return;
    }
    setSession(stored);

    let cancelled = false;
    (async () => {
      try {
        await authApi.validateSession(stored.token);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          clearSession();
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback((next: Session) => {
    saveSession(next);
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ session, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext debe usarse dentro de <AuthProvider>");
  return ctx;
}
